import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarDays, Plus, X, Loader2, Search, AlertTriangle,
  CheckCircle2, ChevronLeft, ChevronRight, RefreshCw, MapPin,
  Inbox, Lock, User as UserIcon, Ban, Clock, ArrowLeft, RotateCcw, Send,
} from "lucide-react";
// Centralized network appointment-type list — shared with CRMQueue's
// Schedule-for-Network-Location modal and the Reschedule modal below.
// See /app/frontend/src/constants/networkAppointmentTypes.js for the
// full list, exclusions (TH / RV), and discipline-mapping notes.
import {
  NETWORK_APPOINTMENT_TYPES_BY_CATEGORY,
  NETWORK_APPOINTMENT_TYPE_DEFAULT,
  isSupportedNetworkApptType,
} from "../constants/networkAppointmentTypes";
import { SLOT_OPTIONS } from "../constants/schedulingSlots";
import { NetworkCalendarGrid, startOfWeek } from "../components/NetworkCalendarGrid";
import { ClinicMonthView } from "../components/ClinicMonthView";
import { mergeCalendarItems } from "../components/directBookings";
import { DirectBookingDetailModal } from "../components/DirectBookingDetailModal";
import { ReservedClaimDialog } from "../components/ReservedClaimDialog";
import { fmtRange, MAX_PROPOSED_TIMES } from "../components/proposalRange";

// "Tue 9:00 AM – 12:00 PM" — chip label for a session draft window.
const draftChipLabel = (r) => {
  const wd = new Date(`${r.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short" });
  return `${wd} ${fmtRange(r)}`;
};

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () =>
  sessionStorage.getItem("token") || localStorage.getItem("token");
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// Reusable grouped <select> for a network appointment type. Identical
// visual shape to the WS-internal Schedule modal so the two modals
// feel like siblings.
function NetworkApptTypeSelect({ value, onChange, testId = "network-appt-type-select" }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
    >
      {NETWORK_APPOINTMENT_TYPES_BY_CATEGORY.map(({ category, types }) => (
        <optgroup key={category} label={category}>
          {types.map((t) => (
            <option key={t.code} value={t.code}>
              {t.code} — {t.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

const STATUS_META = {
  pending: { label: "Pending", ring: "bg-blue-100 text-blue-700 border-blue-200" },
  suggested_alternative: { label: "Alt Suggested", ring: "bg-amber-100 text-amber-800 border-amber-300" },
  locked_in: { label: "Locked In", ring: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", ring: "bg-slate-100 text-slate-500 border-slate-200" },
};

// ── Helpers ─────────────────────────────────────────────────────────────────
function formatApiError(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d))).join("; ");
  }
  if (typeof detail === "object") return detail.msg || detail.message || JSON.stringify(detail);
  return String(detail);
}

// Build the toast string after a network book/confirm/lock-in based on
// the raintree_synced / raintree_error fields surfaced by the backend.
// Used by both WS lock-in and Clinic confirm to keep wording consistent.
function showSchedulingToast(baseMsg, data) {
  const synced = data?.raintree_synced;
  const err = data?.raintree_error;
  if (synced) {
    toast.success(`${baseMsg} · Raintree notified`);
    return;
  }
  if (!err) {
    toast.success(baseMsg);
    return;
  }
  // Distinguish a "real" Raintree refusal (success:false with a message)
  // from a transient webhook/network failure so staff know whether to
  // call the clinic directly.
  const errStr = String(err);
  if (/timeout|connect|status code|n8n|http/i.test(errStr)) {
    toast.warning(`${baseMsg} · Raintree sync pending (saved locally)`);
  } else {
    toast.warning(`${baseMsg} · Raintree sync failed: ${errStr}`);
  }
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function fmtTime(hms) {
  if (!hms) return "";
  const [h, m] = hms.split(":");
  const hr = parseInt(h, 10);
  const suf = hr >= 12 ? "PM" : "AM";
  const h12 = ((hr + 11) % 12) + 1;
  return `${h12}:${m} ${suf}`;
}

function fmtDayLabel(d) {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" });
}

// ── StatusBadge ─────────────────────────────────────────────────────────────
function StatusBadge({ value }) {
  const m = STATUS_META[value] || { label: value, ring: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <span
      data-testid={`status-badge-${value}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${m.ring}`}
    >
      {m.label}
    </span>
  );
}

// ── Patient picker (debounced) ──────────────────────────────────────────────
function PatientPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/crm/patients`, {
          params: { search: q, per_page: 20 },
          headers: authHeaders(),
          signal: ctl.signal,
        });
        setResults(res.data?.patients || res.data?.items || []);
      } catch (e) {
        if (!axios.isCancel(e)) setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      ctl.abort();
    };
  }, [q]);

  if (value) {
    return (
      <div
        data-testid="selected-patient"
        className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50"
      >
        <div className="flex items-center gap-2 min-w-0">
          <UserIcon size={14} className="text-emerald-700 flex-shrink-0" />
          <span className="text-sm font-medium text-emerald-900 truncate">
            {value.first_name} {value.last_name}
          </span>
          <span className="text-xs text-emerald-700">#{value.id}</span>
        </div>
        <button
          type="button"
          data-testid="clear-patient-btn"
          onClick={() => onChange(null)}
          className="text-emerald-700 hover:text-emerald-900"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
        <Search size={14} className="text-slate-400" />
        <input
          data-testid="patient-search-input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search patient by name or phone…"
          className="flex-1 text-sm outline-none bg-transparent"
        />
        {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <div
          data-testid="patient-search-results"
          className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`patient-result-${p.id}`}
              onClick={() => {
                onChange(p);
                setQ("");
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 border-slate-100"
            >
              <div className="text-sm font-medium text-slate-900">
                {p.first_name} {p.last_name}
              </div>
              <div className="text-xs text-slate-500">
                #{p.id} {p.phone ? `• ${p.phone}` : ""} {p.assigned_clinic ? `• ${p.assigned_clinic}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Post Availability Modal ─────────────────────────────────────────────────
// `prefill` is set when the modal is opened from a CRM Queue handoff. It
// locks the patient (staff arrived from a specific record so they can't
// pick a different one) and pre-fills appointment type + notes.
function PostAvailabilityModal({ open, onClose, locationCode, locationName, onCreated, prefill, prefillTimes }) {
  const [patient, setPatient] = useState(null);
  const [apptType, setApptType] = useState(NETWORK_APPOINTMENT_TYPE_DEFAULT);
  const [notes, setNotes] = useState("");
  const [times, setTimes] = useState([{ date: "", start_time: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      if (prefill?.patient_id) {
        setPatient({
          id: Number(prefill.patient_id),
          first_name: prefill.first_name || "",
          last_name: prefill.last_name || "",
          locked: true,
        });
        // Prefer the pre-filled appointment type, but only if it's one
        // we still surface in the dropdown. Legacy/unsupported codes
        // (TH*, RV*) fall back to PTEVA so the modal mounts cleanly.
        const pre = prefill.appointment_type;
        setApptType(pre && isSupportedNetworkApptType(pre) ? pre : NETWORK_APPOINTMENT_TYPE_DEFAULT);
        // Compose a useful clinic-context note from case + body parts
        const noteParts = [];
        if (prefill.case_type) noteParts.push(prefill.case_type);
        if (prefill.body_parts) noteParts.push(prefill.body_parts);
        setNotes(noteParts.join(" — "));
      } else {
        setPatient(null);
        setApptType(NETWORK_APPOINTMENT_TYPE_DEFAULT);
        setNotes("");
      }
      // Two-click calendar ranges land here prefilled for review/submit
      setTimes(
        prefillTimes?.length
          ? prefillTimes.map((t) => ({ date: t.date, start_time: t.start_time, end_time: t.end_time }))
          : [{ date: "", start_time: "" }]
      );
      setErr("");
    }
  }, [open, prefill, prefillTimes]);

  if (!open) return null;

  const patientLocked = !!prefill?.patient_id;

  const addRow = () => {
    if (times.length >= MAX_PROPOSED_TIMES) return;
    setTimes([...times, { date: "", start_time: "" }]);
  };
  const removeRow = (i) => setTimes(times.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => {
    const next = [...times];
    next[i] = { ...next[i], [field]: value };
    setTimes(next);
  };

  const canSubmit =
    !!patient &&
    !!locationCode &&
    times.length > 0 &&
    times.every((t) => t.date && t.start_time);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setErr("");
    try {
      const res = await axios.post(
        `${API}/api/scheduling/availability`,
        {
          patient_id: patient.id,
          location_code: locationCode,
          appointment_type: apptType,
          notes: notes || null,
          proposed_times: times.map((t) => ({ date: t.date, start_time: t.start_time, end_time: t.end_time || undefined })),
        },
        { headers: authHeaders() }
      );
      onCreated?.(res.data);
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e, "Failed to post availability"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="post-availability-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4"
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Post Availability</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              <MapPin size={11} className="inline mr-1" />
              {locationName || locationCode}
            </p>
          </div>
          <button data-testid="close-post-modal" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Patient</label>
            {patientLocked ? (
              <div
                data-testid="locked-patient"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50"
                title="Patient pre-selected from CRM Queue"
              >
                <UserIcon size={14} className="text-slate-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-900 truncate">
                  {patient?.first_name} {patient?.last_name}
                </span>
                {patient?.id && <span className="text-xs text-slate-500">#{patient.id}</span>}
                <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-wide bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                  From CRM Queue
                </span>
              </div>
            ) : (
              <PatientPicker value={patient} onChange={setPatient} />
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Appointment Type</label>
            <NetworkApptTypeSelect
              value={apptType}
              onChange={setApptType}
              testId="post-availability-appt-type"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-700">Proposed Times (1–5)</label>
              <button
                type="button"
                data-testid="add-time-row"
                onClick={addRow}
                disabled={times.length >= MAX_PROPOSED_TIMES}
                className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40 inline-flex items-center gap-1"
              >
                <Plus size={12} /> Add time
              </button>
            </div>
            <div className="space-y-2">
              {times.map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`time-row-${i}`}>
                  <input
                    type="date"
                    value={t.date}
                    onChange={(e) => updateRow(i, "date", e.target.value)}
                    data-testid={`time-date-${i}`}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <select
                    value={t.start_time}
                    onChange={(e) => updateRow(i, "start_time", e.target.value)}
                    data-testid={`time-start-${i}`}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white"
                  >
                    <option value="">Select time…</option>
                    {SLOT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <select
                    value={t.end_time || ""}
                    onChange={(e) => updateRow(i, "end_time", e.target.value)}
                    data-testid={`time-end-${i}`}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white"
                  >
                    <option value="">End (optional)</option>
                    {SLOT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {times.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      data-testid={`remove-time-${i}`}
                      className="text-slate-400 hover:text-rose-600 p-2"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Each block is a 15-minute slot the clinic can pick from.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Notes (optional)</label>
            <textarea
              data-testid="notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any context for the clinic (mobility, special equipment, etc.)"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          {err && (
            <div data-testid="post-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button
            type="button"
            data-testid="cancel-post-btn"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="submit-availability-btn"
            disabled={!canSubmit || busy}
            onClick={submit}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Post Availability
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reschedule Modal (WestStar) ─────────────────────────────────────────────
// WS proposes 1-5 new times with an optional reason. Backend cancels the old
// appointment + Raintree-cancel webhook + creates a new pending request that
// lands in the clinic's pending queue.
function RescheduleModal({ open, request, onClose, onDone }) {
  const [times, setTimes] = useState([{ date: "", start_time: "" }]);
  const [reason, setReason] = useState("");
  const [apptType, setApptType] = useState(NETWORK_APPOINTMENT_TYPE_DEFAULT);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setTimes([{ date: "", start_time: "" }]);
      setReason("");
      setErr("");
      // Preserve the original request's appointment type — fall back to
      // PTEVA only if the original was a now-excluded code (TH*/RV*) so
      // the dropdown can mount with a valid selection.
      const orig = request?.appointment_type;
      setApptType(orig && isSupportedNetworkApptType(orig) ? orig : NETWORK_APPOINTMENT_TYPE_DEFAULT);
    }
  }, [open, request?.appointment_type]);

  if (!open || !request) return null;

  const confirmedTime = (request.proposed_times || []).find((p) => p.is_confirmed) || null;
  const validTimes = times.filter(t => t.date && t.start_time);
  const canSubmit = validTimes.length > 0 && !busy;

  const addRow = () => times.length < 5 && setTimes([...times, { date: "", start_time: "" }]);
  const removeRow = (i) => setTimes(times.filter((_, idx) => idx !== i));
  const update = (i, f, v) => { const n = [...times]; n[i] = { ...n[i], [f]: v }; setTimes(n); };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      await axios.put(
        `${API}/api/scheduling/availability/${request.id}/reschedule`,
        {
          reason: reason.trim() || null,
          appointment_type: apptType,
          proposed_times: validTimes.map(t => ({ date: t.date, start_time: t.start_time })),
        },
        { headers: authHeaders() }
      );
      onDone?.();
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e, "Reschedule failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="reschedule-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
              <RotateCcw size={18} className="text-amber-600" />
              Reschedule Appointment
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {request.patient_name || `Patient #${request.patient_id}`} · {request.appointment_type}
            </p>
          </div>
          <button
            data-testid="reschedule-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {confirmedTime && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700">
              <span className="text-xs text-slate-500 uppercase tracking-wide mr-1.5">Cancelling</span>
              {new Date(`${confirmedTime.date}T00:00:00`).toLocaleDateString("en-US", {
                weekday: "short", month: "short", day: "numeric", year: "numeric",
              })}{" "}
              at {fmtTime(confirmedTime.start_time)}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Reason (optional)
            </label>
            <textarea
              data-testid="reschedule-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Patient called requesting a later time"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Appointment Type
            </label>
            <NetworkApptTypeSelect
              value={apptType}
              onChange={setApptType}
              testId="reschedule-appt-type"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-700">
                New proposed times (1–5, 15 min each) <span className="text-rose-600">*</span>
              </label>
              <button
                type="button"
                data-testid="reschedule-add-time"
                onClick={addRow}
                disabled={times.length >= MAX_PROPOSED_TIMES}
                className="text-xs text-amber-700 hover:text-amber-900 disabled:opacity-40 inline-flex items-center gap-1"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="space-y-2">
              {times.map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`reschedule-time-row-${i}`}>
                  <input
                    type="date"
                    value={t.date}
                    onChange={(e) => update(i, "date", e.target.value)}
                    data-testid={`reschedule-date-${i}`}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                  />
                  <select
                    value={t.start_time}
                    onChange={(e) => update(i, "start_time", e.target.value)}
                    data-testid={`reschedule-start-${i}`}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 bg-white"
                  >
                    <option value="">Select time…</option>
                    {SLOT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {times.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="text-slate-400 hover:text-rose-600 p-2"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          {err && (
            <div data-testid="reschedule-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">
            Keep appointment
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="reschedule-submit"
            className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            <RotateCcw size={14} /> Submit Reschedule
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Request Detail Modal ────────────────────────────────────────────────────
function RequestDetailModal({ open, requestId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/api/scheduling/availability/${requestId}`, {
        headers: authHeaders(),
      });
      setData(res.data);
    } catch (e) {
      setErr(formatApiError(e, "Failed to load request"));
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  const lockIn = async (proposedTimeId) => {
    setBusy(true);
    setErr("");
    try {
      const res = await axios.put(
        `${API}/api/scheduling/availability/${data.id}/lock-in`,
        { proposed_time_id: proposedTimeId },
        { headers: authHeaders() }
      );
      const pname = data?.patient_name || `Patient #${data?.patient_id ?? ""}`;
      showSchedulingToast(`Appointment locked in for ${pname}`, res.data);
      onChanged?.();
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e, "Lock-in failed"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setErr("");
    try {
      await axios.patch(
        `${API}/api/scheduling/availability/${data.id}/cancel`,
        { reason: cancelReason.trim() || null },
        { headers: authHeaders() }
      );
      setCancelOpen(false);
      setCancelReason("");
      onChanged?.();
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e, "Cancel failed"));
    } finally {
      setBusy(false);
    }
  };

  // Cancellation is allowed for any non-cancelled request. Cancelling a
  // locked_in (confirmed) appt also fires the Raintree-cancel webhook and
  // deletes the matching crm_appointments row — handled server-side.
  const canCancel = data && data.status !== "cancelled";
  const isLockedIn = data && data.status === "locked_in";
  // Clinic notes (Merritt's Sep 25 ticket): the LATEST suggest-alternative note, shown above
  // Proposed Times. Plain text only; a missing or empty note shows nothing.
  const clinicNote = (() => {
    const rows = (data?.action_log || []).filter(
      (L) => L.action === "suggested_alternative" && typeof L.details?.notes === "string" && L.details.notes.trim()
    );
    return rows.length ? rows[rows.length - 1] : null;
  })();
  const confirmedTime = data?.proposed_times?.find?.((p) => p.is_confirmed) || null;

  return (
    <div
      data-testid="request-detail-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4"
    >
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900">Request #{requestId}</h2>
              {data && <StatusBadge value={data.status} />}
              {data?.raintree_booking_failed === true && (
                <span
                  data-testid="raintree-booking-failed"
                  className="text-[11px] px-2 py-0.5 rounded-full border font-medium bg-rose-50 text-rose-800 border-rose-200"
                >
                  Raintree booking failed
                </span>
              )}
            </div>
            {data && (
              <p className="text-xs text-slate-500 mt-0.5">
                {data.patient_name || `Patient #${data.patient_id}`} · {data.appointment_type}
              </p>
            )}
          </div>
          <button data-testid="close-detail-modal" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          )}
          {err && (
            <div data-testid="detail-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
          {data && (
            <>
              {data.notes && (
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1">WestStar notes (sent to clinic)</div>
                  <div className="text-sm text-slate-600 bg-slate-50 rounded px-3 py-2 border border-slate-200">
                    {data.notes}
                  </div>
                </div>
              )}

              {clinicNote && (
                <div data-testid="clinic-note-callout" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <div className="text-xs font-medium text-amber-900 mb-1">Clinic note</div>
                  <div className="text-sm text-amber-900 whitespace-pre-wrap break-words">{clinicNote.details.notes}</div>
                  <div className="text-[11px] text-amber-800 mt-1">
                    {clinicNote.performed_by}
                    {clinicNote.created_at ? ` · ${new Date(clinicNote.created_at).toLocaleString()}` : ""}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs font-medium text-slate-700 mb-2">Proposed Times</div>
                <div className="space-y-2">
                  {data.proposed_times.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`proposed-time-${p.id}`}
                      className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border ${
                        p.is_confirmed
                          ? "bg-emerald-50 border-emerald-200"
                          : p.source === "network"
                          ? "bg-amber-50 border-amber-200"
                          : "bg-white border-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <Clock size={13} className="text-slate-400" />
                        <span className="font-medium text-slate-900">
                          {new Date(`${p.date}T00:00:00`).toLocaleDateString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                        <span className="text-slate-500">
                          {fmtTime(p.start_time)} – {fmtTime(p.end_time)}
                        </span>
                        <span
                          className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                            p.source === "network" ? "bg-amber-200 text-amber-900"
                              : p.source === "patient" ? "bg-violet-100 text-violet-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                          data-testid={`proposed-time-source-${p.id}`}
                        >
                          {p.source}
                        </span>
                      </div>
                      {p.is_confirmed && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircle2 size={13} /> Confirmed
                        </span>
                      )}
                      {!p.is_confirmed && p.source === "network" && data.status === "suggested_alternative" && (
                        <button
                          type="button"
                          data-testid={`lock-in-${p.id}`}
                          disabled={busy}
                          onClick={() => lockIn(p.id)}
                          className="px-3 py-1 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          <Lock size={11} /> Lock In
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {data.action_log && data.action_log.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-2">Activity</div>
                  <ul className="space-y-1.5">
                    {data.action_log.map((L) => (
                      <li key={L.id} className="text-xs text-slate-600">
                        <span className="font-medium text-slate-800">{L.action}</span>
                        {" · "}
                        {L.performed_by}
                        {" · "}
                        {L.created_at ? new Date(L.created_at).toLocaleString() : ""}
                        {typeof L.details?.notes === "string" && L.details.notes.trim() && (
                          <div data-testid={`activity-note-${L.id}`} className="mt-1 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1 whitespace-pre-wrap break-words">
                            {L.details.notes}
                          </div>
                        )}
                        {L.action === "cancelled" && typeof L.details?.reason === "string" && L.details.reason.trim() && (
                          <div data-testid={`activity-reason-${L.id}`} className="mt-1 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1 whitespace-pre-wrap break-words">
                            {L.details.reason}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            {canCancel && (
              <button
                type="button"
                data-testid="cancel-request-btn"
                disabled={busy}
                onClick={() => { setCancelReason(""); setCancelOpen(true); }}
                className={
                  isLockedIn
                    ? "px-3 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50"
                    : "px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50"
                }
              >
                <Ban size={14} /> {isLockedIn ? "Cancel Appointment" : "Cancel Request"}
              </button>
            )}
            {isLockedIn && (
              <button
                type="button"
                data-testid="reschedule-btn"
                disabled={busy}
                onClick={() => setRescheduleOpen(true)}
                className="px-3 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <RotateCcw size={14} /> Reschedule
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>

      {cancelOpen && data && (
        <div
          data-testid="cancel-confirm-modal"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4"
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
                <AlertTriangle size={18} className="text-rose-600" />
                {isLockedIn ? "Cancel Appointment?" : "Cancel Request?"}
              </h3>
              <button
                data-testid="cancel-confirm-close"
                onClick={() => setCancelOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-slate-700">
                {isLockedIn ? (
                  <>
                    Are you sure you want to cancel this appointment for{" "}
                    <strong>{data.patient_name || `Patient #${data.patient_id}`}</strong> at{" "}
                    <strong>{data.location_code}</strong>
                    {confirmedTime && (
                      <>
                        {" "}on{" "}
                        <strong>
                          {new Date(`${confirmedTime.date}T00:00:00`).toLocaleDateString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </strong>{" "}
                        at <strong>{fmtTime(confirmedTime.start_time)}</strong>
                      </>
                    )}
                    ? This will also notify Raintree.
                  </>
                ) : (
                  <>
                    Cancel availability request for{" "}
                    <strong>{data.patient_name || `Patient #${data.patient_id}`}</strong>?
                    This cannot be undone.
                  </>
                )}
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Internal cancellation note {isLockedIn ? "(optional, written to patient notes)" : "(optional)"}
                </label>
                <textarea
                  data-testid="cancel-reason-input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Patient called to reschedule for next week"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
                />
              </div>
              {err && (
                <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {err}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
              <button
                type="button"
                onClick={() => setCancelOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
              >
                Keep
              </button>
              <button
                type="button"
                data-testid="cancel-confirm-btn"
                disabled={busy}
                onClick={cancel}
                className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-2"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Yes, cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <RescheduleModal
        open={rescheduleOpen}
        request={data}
        onClose={() => setRescheduleOpen(false)}
        onDone={() => { setRescheduleOpen(false); onChanged?.(); onClose?.(); }}
      />
    </div>
  );
}

// ── Action Queue (cross-location) ───────────────────────────────────────────
// ── Action Queue — confirmed appointments needing acknowledgment ────────────
// This existing amber surface (previously fed by /weststar-queue, which
// never populated it in practice) now renders the confirmed-appointments
// queue from GET /api/scheduling/action-queue. Cross-location by design.
// Scale-ready: fixed footprint — ~6 compact rows visible with internal
// scroll, so 100+ items never grow the page. The count chip shows the TRUE
// total; the server caps rows at 200 ("showing 200 of N" note) and Mark All
// Acknowledged is the bulk-recovery path (no pagination by design).
function ActionQueue({ items, total, locations, onOpen, onRefresh, onAcknowledge, onAcknowledgeAll }) {
  const locLabel = (code) => {
    const l = locations.find((x) => x.location_code === code);
    return l ? `${l.name}${l.city ? ` · ${l.city}` : ""}` : code;
  };
  const fmtConfirmed = (r) => {
    if (!r.confirmed_date) return "—";
    const d = new Date(`${r.confirmed_date}T00:00:00`);
    const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return r.confirmed_start_time ? `${day} ${fmtTime(r.confirmed_start_time)}` : day;
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="action-queue">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-amber-50">
        <div className="flex items-center gap-2">
          <Inbox size={15} className="text-amber-700" />
          <h3 className="text-sm font-semibold text-amber-900">Action Queue</h3>
          <span className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full" data-testid="action-queue-count">
            {total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <button
              type="button"
              data-testid="ack-all-btn"
              onClick={onAcknowledgeAll}
              className="px-2.5 py-1 text-[11px] font-semibold text-amber-800 bg-white border border-amber-300 rounded-lg hover:bg-amber-100"
            >
              Mark All Acknowledged
            </button>
          )}
          <button
            type="button"
            data-testid="refresh-queue-btn"
            onClick={onRefresh}
            className="text-amber-700 hover:text-amber-900"
            title="Refresh"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>
      {items.length < total && (
        <div
          className="px-4 py-1.5 text-[11px] text-amber-700 bg-amber-50/60 border-b border-amber-100"
          data-testid="action-queue-cap-note"
        >
          showing {items.length} of {total}
        </div>
      )}
      <div className="max-h-[320px] overflow-y-auto" data-testid="action-queue-scroll">
        {items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="action-queue-empty">
            All clear — no confirmations awaiting acknowledgment.
          </div>
        )}
        {items.map((r) => (
          <div
            key={r.id}
            data-testid={`action-queue-row-${r.id}`}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(r)}
            title="Open this patient in the CRM Queue"
            className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 cursor-pointer"
          >
            <div className="flex-1 min-w-0 truncate text-xs text-slate-600">
              <span className="text-sm font-medium text-slate-900">{r.patient_name || `Patient #${r.patient_id}`}</span>
              <span className="text-slate-400"> · </span>
              {fmtConfirmed(r)}
              <span className="text-slate-400"> · </span>
              {locLabel(r.location_code)}
            </div>
            <button
              type="button"
              data-testid={`ack-btn-${r.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onAcknowledge(r.id);
              }}
              className="flex-shrink-0 px-2.5 py-1 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md"
            >
              Acknowledge
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function NetworkScheduling() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [locationCode, setLocationCode] = useState("");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [requests, setRequests] = useState([]);
  // Action Queue (confirmed appointments) — rows are server-capped at 200;
  // confirmedTotal is the TRUE count driving the chip.
  const [confirmedQueue, setConfirmedQueue] = useState([]);
  const [confirmedTotal, setConfirmedTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [postOpen, setPostOpen] = useState(false);
  const [detailRequestId, setDetailRequestId] = useState(null);
  // View toggle — Month (full-month grid, default; replaced the old List
  // week pills 2026-08, same shared component as the clinic portal) |
  // Calendar (read-only 7am–7pm time grid + propose flow, untouched).
  const [viewMode, setViewMode] = useState("month");
  // Month view follows the tab's existing model: the SINGLE clinic selected
  // in the location dropdown (all-network month scope is deferred to a
  // future ticket behind a proper single cross-location endpoint).
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [monthRequests, setMonthRequests] = useState([]);
  const [monthLoading, setMonthLoading] = useState(false);
  // Reserved WestStar slots (Phase 2, 2026-02) — instances for the visible
  // range in the selected clinic. Internal side supports OPEN → claim.
  const [reservedWeek, setReservedWeek] = useState([]);
  const [reservedMonth, setReservedMonth] = useState([]);
  const [claimSlot, setClaimSlot] = useState(null); // an instance obj
  // Set by calendar block click → switch to List and highlight that card.
  const [highlightRequestId, setHighlightRequestId] = useState(null);
  // CRM Queue → Network Scheduling handoff. Parsed once on mount and held
  // in state so a URL clear after submit doesn't blank the locked patient
  // mid-flow. Null when staff lands here directly from the sidebar.
  const [prefill, setPrefill] = useState(null);
  // Range pivot (2026-06): two-click ranges from the calendar prefill the
  // EXISTING propose form — the form stays the single submit path; the
  // clicks are input only.
  const [rangeTimes, setRangeTimes] = useState(null);
  // Propose session (2026-06): the CRM "Schedule at Network Clinic" action
  // deep-links here with view=calendar — staff accumulate up to 3 draft
  // range windows on the calendar for the handed-off patient, then submit
  // through the EXISTING form/endpoint. View-only state — abandon persists
  // nothing. Non-session calendar clicks keep the shipped behavior.
  const [sessionActive, setSessionActive] = useState(false);
  const [draftRanges, setDraftRanges] = useState([]);
  const sessionFull = sessionActive && draftRanges.length >= MAX_PROPOSED_TIMES;
  const handleRangeComplete = (range) => {
    if (sessionActive) {
      setDraftRanges((prev) => (prev.length >= MAX_PROPOSED_TIMES ? prev : [...prev, range]));
      return;
    }
    setRangeTimes((prev) => [...(prev || []), range].slice(0, MAX_PROPOSED_TIMES)); // proposal cap (mirrors server)
    setPostOpen(true);
  };
  const exitSession = useCallback(() => {
    setSessionActive(false);
    setDraftRanges([]);
    setPrefill(null);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [postSubmitBanner, setPostSubmitBanner] = useState(null);

  const currentLocation = useMemo(
    () => locations.find((l) => l.location_code === locationCode),
    [locations, locationCode]
  );

  // Parse query params once on mount. Subsequent URL changes (e.g. user
  // clears the URL after submit) don't reopen the modal.
  useEffect(() => {
    const pid = searchParams.get("patient_id");
    if (!pid) return;
    const fullName = (searchParams.get("patient_name") || "").trim();
    const parts = fullName.split(/\s+/);
    setPrefill({
      patient_id: pid,
      first_name: parts[0] || "",
      last_name: parts.slice(1).join(" ") || "",
      location_code: searchParams.get("location_code") || "",
      appointment_type: searchParams.get("appointment_type") || NETWORK_APPOINTMENT_TYPE_DEFAULT,
      case_type: searchParams.get("case_type") || "",
      body_parts: searchParams.get("body_parts") || "",
    });
    // view=calendar (CRM "Schedule at Network Clinic", 2026-06): open in
    // Calendar view with a propose session instead of the List-view modal.
    if (searchParams.get("view") === "calendar") {
      setViewMode("calendar");
      setSessionActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load locations once
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/scheduling/locations`, { headers: authHeaders() });
        const locs = res.data?.locations || [];
        setLocations(locs);
        // If a handoff specified a location_code, prefer that.
        const pre = searchParams.get("location_code");
        const match = pre && locs.find((l) => l.location_code === pre);
        if (match) {
          setLocationCode(match.location_code);
        } else if (locs.length > 0 && !locationCode) {
          setLocationCode(locs[0].location_code);
        }
      } catch (e) {
        setErr(formatApiError(e, "Failed to load locations"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the Post Availability modal once a handoff has landed AND
  // the matching location is selected (so the modal header shows the right
  // clinic name). Skipped in a propose session — there the modal opens
  // only from "Send to clinic".
  useEffect(() => {
    if (prefill && locationCode && !postOpen && !postSubmitBanner && !sessionActive) {
      setPostOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, locationCode]);

  // Escape (with no range anchor in progress) exits the propose session.
  // The grid's own Escape handler clears an active anchor; the anchor
  // marker in the DOM is the tell that this keypress belongs to the grid.
  useEffect(() => {
    if (!sessionActive) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || postOpen) return;
      if (document.querySelector('[data-testid="propose-anchor-marker"]')) return;
      exitSession();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sessionActive, postOpen, exitSession]);

  const loadCalendar = useCallback(async () => {
    if (!locationCode) return;
    setLoading(true);
    setErr("");
    try {
      const startISO = toISODate(weekStart);
      const endISO = toISODate(addDays(weekStart, 6));
      const res = await axios.get(`${API}/api/scheduling/calendar/${locationCode}`, {
        params: { start_date: startISO, end_date: endISO },
        headers: authHeaders(),
      });
      setRequests(mergeCalendarItems(res.data));
      // Reserved slots for the same clinic + range — additive; a failure
      // here does not disturb the calendar itself.
      try {
        const rs = await axios.get(`${API}/api/scheduling/reserved-slots/instances`, {
          params: { location_codes: locationCode, start_date: startISO, end_date: endISO },
          headers: authHeaders(),
        });
        setReservedWeek(rs.data?.instances || []);
      } catch (_e) {
        setReservedWeek([]);
      }
    } catch (e) {
      setErr(formatApiError(e, "Failed to load calendar"));
    } finally {
      setLoading(false);
    }
  }, [locationCode, weekStart]);

  // Month fetch — ONE server-side range query for the SELECTED location
  // (the same endpoint the week view uses, month range; results are
  // complete — no client slicing of truncated fetches). Refetches on
  // dropdown change and month navigation. The 42-cell grid spans
  // adjacent-month spillover days, so the fetched range is the full grid.
  const loadMonth = useCallback(async () => {
    if (!locationCode) return;
    setMonthLoading(true);
    setErr("");
    try {
      const gridStart = startOfWeek(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1));
      const res = await axios.get(`${API}/api/scheduling/calendar/${locationCode}`, {
        params: {
          start_date: toISODate(gridStart),
          end_date: toISODate(addDays(gridStart, 41)),
        },
        headers: authHeaders(),
      });
      setMonthRequests(mergeCalendarItems(res.data));
      try {
        const rs = await axios.get(`${API}/api/scheduling/reserved-slots/instances`, {
          params: {
            location_codes: locationCode,
            start_date: toISODate(gridStart),
            end_date: toISODate(addDays(gridStart, 41)),
          },
          headers: authHeaders(),
        });
        setReservedMonth(rs.data?.instances || []);
      } catch (_e) {
        setReservedMonth([]);
      }
    } catch (e) {
      setErr(formatApiError(e, "Failed to load month"));
    } finally {
      setMonthLoading(false);
    }
  }, [locationCode, monthAnchor]);

  useEffect(() => {
    if (viewMode === "month") loadMonth();
  }, [viewMode, loadMonth]);

  // Action Queue load — was GET /api/scheduling/weststar-queue (suggested-
  // alternative requests; that endpoint still exists server-side but has no
  // frontend consumer now). The queue is cross-location and ignores the
  // location select above by design (server-side too).
  const loadConfirmedQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/scheduling/action-queue`, { headers: authHeaders() });
      setConfirmedQueue(res.data?.requests || []);
      setConfirmedTotal(res.data?.total || 0);
    } catch (e) {
      // queue errors aren't fatal; just log
      console.warn("Failed to load action queue:", formatApiError(e));
    }
  }, []);

  const acknowledgeConfirmed = async (id) => {
    try {
      await axios.post(`${API}/api/scheduling/action-queue/${id}/acknowledge`, {}, { headers: authHeaders() });
      setConfirmedQueue((q) => q.filter((i) => i.id !== id));
      setConfirmedTotal((t) => Math.max(0, t - 1));
      window.dispatchEvent(new Event("action-queue-refresh")); // sidebar badge
    } catch (e) {
      toast.error(formatApiError(e, "Failed to acknowledge"));
    }
  };

  const acknowledgeAllConfirmed = async () => {
    try {
      await axios.post(`${API}/api/scheduling/action-queue/acknowledge-all`, {}, { headers: authHeaders() });
      setConfirmedQueue([]);
      setConfirmedTotal(0);
      window.dispatchEvent(new Event("action-queue-refresh"));
    } catch (e) {
      toast.error(formatApiError(e, "Failed to acknowledge all"));
    }
  };

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  useEffect(() => {
    loadConfirmedQueue();
  }, [loadConfirmedQueue]);

  const refreshAll = () => {
    loadCalendar();
    loadConfirmedQueue();
    if (viewMode === "month") loadMonth();
  };

  // Calendar block click = navigation only: flip to Month (the List's
  // replacement), scroll the request's chip into view, pulse a highlight
  // ring. No actions. Month anchor follows the week being viewed.
  const handleBlockClick = (requestId) => {
    setMonthAnchor(new Date(weekStart.getFullYear(), weekStart.getMonth(), 1));
    setViewMode("month");
    setHighlightRequestId(requestId);
  };

  useEffect(() => {
    if (viewMode !== "month" || !highlightRequestId) return;
    const el = document.querySelector(`[data-testid^="month-chip-${highlightRequestId}-"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightRequestId(null), 4000);
    return () => clearTimeout(t);
  }, [viewMode, highlightRequestId, monthRequests]);

  const weekLabel = useMemo(() => {
    const end = addDays(weekStart, 6);
    return `${weekStart.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  }, [weekStart]);

  const monthLabel = useMemo(
    () => monthAnchor.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    [monthAnchor]
  );

  // Direct-booking chips route to the shared read-only modal — the "db-"
  // id prefix can never collide with a numeric request id.
  const isDirectDetailId = typeof detailRequestId === "string" && detailRequestId.startsWith("db-");
  const directDetailItem = isDirectDetailId
    ? monthRequests.find((r) => r.id === detailRequestId) ||
      requests.find((r) => r.id === detailRequestId) ||
      null
    : null;

  return (
    <div className="p-6 space-y-5" data-testid="network-scheduling-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays size={22} className="text-blue-600" />
            Network Scheduling
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Coordinate patient visits with partner clinics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex rounded-lg border border-slate-300 overflow-hidden"
            data-testid="view-toggle"
          >
            <button
              type="button"
              data-testid="view-toggle-month"
              onClick={() => setViewMode("month")}
              className={`px-3 py-2 text-sm font-medium ${
                viewMode === "month"
                  ? "bg-slate-800 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              Month
            </button>
            <button
              type="button"
              data-testid="view-toggle-calendar"
              onClick={() => {
                setViewMode("calendar");
                setHighlightRequestId(null);
              }}
              className={`px-3 py-2 text-sm font-medium border-l border-slate-300 ${
                viewMode === "calendar"
                  ? "bg-slate-800 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              Calendar
            </button>
          </div>
          <button
            type="button"
            data-testid="open-post-modal"
            disabled={!locationCode}
            onClick={() => setPostOpen(true)}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-2"
          >
            <Plus size={16} /> Post Availability
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
        {/* Calendar pane */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <MapPin size={14} className="text-slate-400 flex-shrink-0" />
              <select
                data-testid="location-select"
                value={locationCode}
                onChange={(e) => setLocationCode(e.target.value)}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 max-w-[300px]"
              >
                {locations.length === 0 && <option value="">No network locations</option>}
                {locations.map((l) => (
                  <option key={l.location_code} value={l.location_code}>
                    {l.name} {l.city ? `· ${l.city}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid={viewMode === "month" ? "prev-month-btn" : "prev-week-btn"}
                onClick={() =>
                  viewMode === "month"
                    ? setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))
                    : setWeekStart(addDays(weekStart, -7))
                }
                className="p-2 hover:bg-slate-100 rounded-lg"
                aria-label={viewMode === "month" ? "Previous month" : "Previous week"}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                data-testid="today-btn"
                onClick={() =>
                  viewMode === "month"
                    ? setMonthAnchor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
                    : setWeekStart(startOfWeek(new Date()))
                }
                className="px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
              >
                Today
              </button>
              <button
                type="button"
                data-testid={viewMode === "month" ? "next-month-btn" : "next-week-btn"}
                onClick={() =>
                  viewMode === "month"
                    ? setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))
                    : setWeekStart(addDays(weekStart, 7))
                }
                className="p-2 hover:bg-slate-100 rounded-lg"
                aria-label={viewMode === "month" ? "Next month" : "Next week"}
              >
                <ChevronRight size={16} />
              </button>
              <button
                type="button"
                data-testid="refresh-calendar-btn"
                onClick={refreshAll}
                className="p-2 hover:bg-slate-100 rounded-lg ml-1"
                aria-label="Refresh"
              >
                <RefreshCw size={14} />
              </button>
              <div
                className="text-sm font-medium text-slate-700 ml-2"
                data-testid={viewMode === "month" ? "month-label" : "week-label"}
              >
                {viewMode === "month" ? monthLabel : weekLabel}
              </div>
            </div>
          </div>

          {err && (
            <div data-testid="calendar-error" className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
              <AlertTriangle size={14} /> {err}
            </div>
          )}

          {viewMode === "calendar" && sessionActive && (
            <div
              data-testid="propose-session-banner"
              className="mb-3 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-blue-900 min-w-0">
                  <span className="font-semibold" data-testid="session-patient-name">
                    Proposing times for{" "}
                    {`${prefill?.first_name || ""} ${prefill?.last_name || ""}`.trim() ||
                      `Patient #${prefill?.patient_id}`}
                  </span>
                  {" — click a start time, then an end time · "}
                  <span className="font-semibold" data-testid="session-window-count">
                    {draftRanges.length} of {MAX_PROPOSED_TIMES} windows
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    data-testid="session-send-btn"
                    disabled={draftRanges.length === 0}
                    onClick={() => {
                      setRangeTimes(draftRanges);
                      setPostOpen(true);
                    }}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-1.5"
                  >
                    <Send size={12} /> Send to clinic
                  </button>
                  <button
                    type="button"
                    data-testid="session-cancel-btn"
                    onClick={exitSession}
                    className="px-3 py-1.5 text-xs font-medium text-blue-800 hover:bg-blue-100 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              {draftRanges.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {draftRanges.map((r, i) => (
                    <span
                      key={`${r.date}-${r.start_time}-${i}`}
                      data-testid={`session-chip-${i}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-blue-300 text-xs font-medium text-blue-900"
                    >
                      {draftChipLabel(r)}
                      <button
                        type="button"
                        data-testid={`session-chip-remove-${i}`}
                        onClick={() =>
                          setDraftRanges((prev) => prev.filter((_, idx) => idx !== i))
                        }
                        className="text-blue-500 hover:text-blue-800 font-bold leading-none"
                        title="Remove this window"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {sessionFull && (
                <div className="mt-2 text-xs text-blue-700" data-testid="session-cap-hint">
                  {MAX_PROPOSED_TIMES} windows max — remove one to add another.
                </div>
              )}
            </div>
          )}

          {(viewMode === "month" ? monthLoading : loading) ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : viewMode === "calendar" ? (
            <NetworkCalendarGrid
              weekStart={weekStart}
              requests={requests}
              onBlockClick={handleBlockClick}
              proposeMode={!sessionFull}
              onRangeComplete={handleRangeComplete}
              draftRanges={sessionActive ? draftRanges : []}
              reservedSlots={reservedWeek}
              reservedMode="internal"
              onReservedClick={setClaimSlot}
            />
          ) : (
            <ClinicMonthView
              monthAnchor={monthAnchor}
              requests={monthRequests}
              onOpen={(r) => setDetailRequestId(r.id)}
              highlightRequestId={highlightRequestId}
              chipColorMode="status"
              rootTestId="network-month-view"
              reservedSlots={reservedMonth}
              reservedMode="internal"
              onReservedClick={setClaimSlot}
            />
          )}

          <div className="flex items-center gap-4 mt-4 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded border border-blue-200 bg-blue-100" /> Pending
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded border border-amber-300 bg-amber-100" /> Alt Suggested
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded border border-emerald-200 bg-emerald-100" /> Locked In
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="legend-direct">
              <span className="w-3 h-3 rounded border border-indigo-300 bg-indigo-100" /> Direct
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="legend-reserved-open">
              <span
                className="w-3 h-3 rounded border-2 border-dashed border-slate-400 bg-slate-50"
                style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(148,163,184,0.35) 0 2px, transparent 2px 4px)" }}
              /> WS Reserved (claimable)
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="legend-reserved-blocked">
              <span
                className="w-3 h-3 rounded border-2 border-dashed border-slate-400 bg-slate-100 opacity-70 line-through text-transparent"
              >—</span> WS Reserved (blocked)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded border border-slate-200 bg-slate-100" /> Cancelled
            </span>
          </div>
        </div>

        {/* Action queue — row click deep-links to the CRM Queue at that
            patient (same /crm-queue?q= prefill Patient Search uses). */}
        <ActionQueue
          items={confirmedQueue}
          total={confirmedTotal}
          locations={locations}
          onOpen={(r) => navigate(`/crm-queue?q=${encodeURIComponent(r.patient_name || "")}`)}
          onRefresh={loadConfirmedQueue}
          onAcknowledge={acknowledgeConfirmed}
          onAcknowledgeAll={acknowledgeAllConfirmed}
        />
      </div>

      <PostAvailabilityModal
        open={postOpen}
        onClose={() => {
          setPostOpen(false);
          setRangeTimes(null);
          // In a propose session, closing the confirm step returns to the
          // session with drafts intact — only Cancel/Escape/submit exit.
          if (prefill && !sessionActive) {
            setPrefill(null);
            setSearchParams({}, { replace: true });
          }
        }}
        locationCode={locationCode}
        locationName={currentLocation?.name}
        prefill={prefill}
        prefillTimes={rangeTimes}
        onCreated={(req) => {
          refreshAll();
          // Successful submit ends the propose session; the real pending
          // blocks render from the calendar reload.
          setSessionActive(false);
          setDraftRanges([]);
          if (prefill) {
            // Post-submit handoff banner — give staff a one-click bounce
            // back to where they were in the CRM Queue. URL is reset so a
            // refresh doesn't reopen the modal.
            setPostSubmitBanner({
              patient_name: `${prefill.first_name} ${prefill.last_name}`.trim() || `Patient #${prefill.patient_id}`,
              request_id: req?.id,
            });
            setPrefill(null);
            setSearchParams({}, { replace: true });
          }
        }}
      />

      {postSubmitBanner && (
        <div
          data-testid="post-submit-banner"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-white border border-emerald-200 shadow-xl rounded-xl px-5 py-4 flex items-center gap-4 max-w-2xl w-[calc(100%-2rem)]"
        >
          <CheckCircle2 size={22} className="text-emerald-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              Availability posted for {postSubmitBanner.patient_name}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              The clinic has been notified. You can hop back to the queue or stay here to monitor.
            </p>
          </div>
          <button
            type="button"
            data-testid="stay-on-calendar-btn"
            onClick={() => setPostSubmitBanner(null)}
            className="px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Stay on Calendar
          </button>
          <button
            type="button"
            data-testid="back-to-crm-queue-btn"
            onClick={() => navigate("/crm-queue")}
            className="px-3 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={13} /> Back to CRM Queue
          </button>
        </div>
      )}

      <RequestDetailModal
        open={!!detailRequestId && !isDirectDetailId}
        requestId={detailRequestId}
        onClose={() => setDetailRequestId(null)}
        onChanged={refreshAll}
      />

      <DirectBookingDetailModal
        open={!!directDetailItem}
        item={directDetailItem}
        onClose={() => setDetailRequestId(null)}
      />

      <ReservedClaimDialog
        open={!!claimSlot}
        slot={claimSlot}
        locationLabel={currentLocation?.name || locationCode}
        onClose={() => setClaimSlot(null)}
        onClaimed={() => { setClaimSlot(null); refreshAll(); }}
        onConflict={refreshAll}
      />
    </div>
  );
}
